function _createForOfIteratorHelper(r, e) { var t = "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (!t) { if (Array.isArray(r) || (t = _unsupportedIterableToArray(r)) || e && r && "number" == typeof r.length) { t && (r = t); var _n = 0, F = function F() {}; return { s: F, n: function n() { return _n >= r.length ? { done: !0 } : { done: !1, value: r[_n++] }; }, e: function e(r) { throw r; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var o, a = !0, u = !1; return { s: function s() { t = t.call(r); }, n: function n() { var r = t.next(); return a = r.done, r; }, e: function e(r) { u = !0, o = r; }, f: function f() { try { a || null == t["return"] || t["return"](); } finally { if (u) throw o; } } }; }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
import DOMPurify from 'dompurify';
import { liftImgStyleDimensions } from './email-style-utils';
var emailSafeConfig = {
  ALLOWED_TAGS: ['a', 'b', 'blockquote', 'br', 'div', 'em',
  // 'font', // Removed: font with color attribute can be used to hide text (e.g. white text on white background)
  'h1', 'h2', 'h3', 'i', 'img', 'li', 'ol', 'ul', 'p', 'span', 'strike', 'strong', 'table', 'tbody', 'td', 'th', 'thead', 'tr', 'u'],
  ALLOWED_ATTR: [
  // 'color', // Removed: only relevant for <font> tag, which is now disallowed
  'dir', 'href', 'src', 'alt', 'title', 'width', 'height', 'style' // Allowed, but the value is filtered to a safe property allowlist by filterStyle (see the uponSanitizeAttribute hook). Blocks color/font-size/positioning/url() etc.
  // 'class', // Removed: allowing class lets email senders apply application CSS classes, risking UI manipulation
  ],
  FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'button'],
  // While what is allowed / not allowed is already restricted in ALLOWED_TAGS and ALLOWED_ATTR, we explicitly have a list of forbidden as well, so that these forbidden tags / attributes would have to be explicitly removed from the forbidden list if they were to be used, as to avoid accidental enabling of dangerous tags / attributes
  FORBID_ATTR: ['srcset'],
  // See comment above. NOTE: 'style' is allowed but sanitized by filterStyle.
  ALLOWED_URI_REGEXP: /^(?!(javascript|vbscript):|(data:(?!image\/)))/i // Blocks known dangerous URI schemes. Allows data:image/ through so the replace callback can handle it (with SVG exclusion there). Custom filtering in EmailBody's replace callback provides the primary allowlist layer.
};

// Inline-style sanitization (default-deny). Only layout/spacing and basic text
// properties are kept — NOTHING that can recolour, hide, or reposition content.
// This lets senders' own spacing (e.g. margin:0 signatures) survive without
// exposing the vectors that motivated stripping style entirely:
//   • color / background → recolouring, white-on-white invisible text
//   • display / visibility / opacity → hiding content
//   • position / z-index / transform / float / clip / text-indent → escaping
//     the message container, overlaying app chrome, or off-screen hiding
//   • width / height → zero-size hiding or huge blow-ups
//   • url(...) → tracking pixels / outbound requests
// font-size IS allowed, but only in absolute units within a readable range (see
// fontSizeIsSafe) so it can't be shrunk to invisibility (1px text smuggling
// prompt-injection payloads) or compounded via relative units.
var ALLOWED_STYLE_PROPERTIES = new Set(['margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left', 'padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left', 'text-align', 'vertical-align', 'line-height', 'font-size', 'font-weight', 'font-style', 'text-decoration', 'text-decoration-line', 'list-style-type', 'list-style-position', 'border', 'border-width', 'border-style', 'border-color', 'border-collapse', 'border-top', 'border-right', 'border-bottom', 'border-left']);

// Properties where a negative value could be used to hide/displace content.
var NEGATIVE_DISALLOWED_PROPERTIES = new Set(['margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left', 'padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left']);

// Tokens that must never appear in a value, regardless of property. calc() is
// blocked outright: no allowlisted property needs it for email layout, and it
// is an easy way to smuggle a negative length past the negative-value check
// below (e.g. `margin: calc(0px-9999px)`).
var UNSAFE_STYLE_VALUE = /url\(|expression\(|javascript:|@import|calc\(|[\\<>{}]|\/\*/i;

// A negative sign at the start of the value, after whitespace, or right after
// an opening paren — covers `-5px`, `0 -5px`, and parenthesised forms.
var NEGATIVE_VALUE = /(^|[\s(])-/;

// `vertical-align` accepts arbitrary lengths (incl. negative) that could shift
// content off-screen, so restrict it to the safe keyword set.
var VERTICAL_ALIGN_KEYWORDS = new Set(['baseline', 'sub', 'super', 'text-top', 'text-bottom', 'middle', 'top', 'bottom']);
var LINE_HEIGHT_VALUE = /^(\d+(?:\.\d+)?)(px|pt|em|rem|%)?$/i;

// `line-height` could be set to 0 (collapsing/overlapping text) or an extreme
// value; constrain it to sane, positive ranges.
var lineHeightIsSafe = function lineHeightIsSafe(value) {
  var _match$;
  if (value.toLowerCase() === 'normal') return true;
  var match = LINE_HEIGHT_VALUE.exec(value);
  if (!match) return false;
  var amount = parseFloat(match[1]);
  var unit = (_match$ = match[2]) === null || _match$ === void 0 ? void 0 : _match$.toLowerCase();
  if (!unit) return amount >= 0.5 && amount <= 3; // unitless multiplier
  if (unit === '%') return amount >= 50 && amount <= 300;
  if (unit === 'em' || unit === 'rem') return amount > 0 && amount <= 4;
  return amount > 0 && amount <= 48; // px / pt
};
var FONT_SIZE_VALUE = /^(\d+(?:\.\d+)?)(px|pt)$/i;

// `font-size` is restricted to absolute units within a readable range. Relative
// units (%, em, rem) and keywords are rejected because they cascade/compound and
// could be driven below the floor (nested 50% → invisible); a floor is the whole
// point, so anything we can't bound is dropped (it falls back to the body size).
var fontSizeIsSafe = function fontSizeIsSafe(value) {
  var match = FONT_SIZE_VALUE.exec(value);
  if (!match) return false;
  var amount = parseFloat(match[1]);
  var unit = match[2].toLowerCase();
  // 8pt ≈ 10.6px, so both floors land around the same readable size.
  return unit === 'pt' ? amount >= 8 && amount <= 36 : amount >= 10 && amount <= 48;
};

// Per-property value validation for properties that, despite being layout-only,
// accept values that could hide or displace content.
var propertyValueIsSafe = function propertyValueIsSafe(property, value) {
  if (property === 'vertical-align') {
    return VERTICAL_ALIGN_KEYWORDS.has(value.toLowerCase());
  }
  if (property === 'line-height') {
    return lineHeightIsSafe(value);
  }
  if (property === 'font-size') {
    return fontSizeIsSafe(value);
  }
  return true;
};

// Filters an inline style string down to the allowlisted properties with
// validated values, rebuilding the declaration from scratch (so the browser
// only ever sees declarations we explicitly emitted). Returns '' if nothing
// safe remains.
export var filterStyle = function filterStyle(style) {
  if (!style) return '';
  var safeDeclarations = [];
  var _iterator = _createForOfIteratorHelper(style.split(';')),
    _step;
  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var declaration = _step.value;
      var colonIndex = declaration.indexOf(':');
      if (colonIndex === -1) continue;
      var property = declaration.slice(0, colonIndex).trim().toLowerCase();
      if (!ALLOWED_STYLE_PROPERTIES.has(property)) continue;
      var value = declaration.slice(colonIndex + 1).replace(/!\s*important/gi, '').trim();
      if (!value || UNSAFE_STYLE_VALUE.test(value)) continue;
      if (NEGATIVE_DISALLOWED_PROPERTIES.has(property) && NEGATIVE_VALUE.test(value)) {
        continue;
      }
      if (!propertyValueIsSafe(property, value)) continue;
      safeDeclarations.push("".concat(property, ": ").concat(value));
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }
  return safeDeclarations.join('; ');
};
export var getSanitizedHtml = function getSanitizedHtml(contents) {
  // Two scoped hooks (registered per call, removed afterwards):
  // • uponSanitizeElement: lift img width/height out of `style` onto the
  //   width/height attributes before the style value is filtered.
  // • uponSanitizeAttribute: filter every `style` value down to the safe
  //   property allowlist; drop the attribute entirely if nothing remains.
  try {
    DOMPurify.addHook('uponSanitizeElement', liftImgStyleDimensions);
    DOMPurify.addHook('uponSanitizeAttribute', function (_node, data) {
      if (data.attrName !== 'style') return;
      data.attrValue = filterStyle(data.attrValue);
      if (!data.attrValue) data.keepAttr = false;
    });
    return DOMPurify.sanitize(contents, emailSafeConfig);
  } catch (error) {
    console.error('Error sanitizing HTML:', error);
    return '';
  } finally {
    DOMPurify.removeHook('uponSanitizeElement');
    DOMPurify.removeHook('uponSanitizeAttribute');
  }
};

// Plain text emails use newlines (\r\n, \n, or \r) for line breaks, but whitespace-normal CSS collapses them.
// If the content has no block-level HTML tags (which handle line breaking in HTML emails),
// we treat it as plain text and convert newlines to <br> so line breaks render correctly.
var BLOCK_LEVEL_HTML_PATTERN = /<(br|p|div|h[1-6]|ul|ol|li|blockquote|table|tbody|thead|tr|td|th)\b/i;
export var convertPlainTextNewlines = function convertPlainTextNewlines(content) {
  if (!content || BLOCK_LEVEL_HTML_PATTERN.test(content)) {
    return content;
  }
  return content.replace(/\r?\n|\r/g, '<br>');
};